<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import type { Resolved, Service } from '@/types'
import { settings, PATHS } from '@/content'
import { useContentHtml } from '@/lib/pageHtml'
import { baseHead, breadcrumbLd } from '@/lib/seo'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'
import MatterportEmbed from '@/components/MatterportEmbed.vue'
import LabelsBand from '@/components/LabelsBand.vue'
import CtaProjet from '@/components/CtaProjet.vue'

const props = defineProps<{ resolved: Resolved }>()
const service = computed(() => props.resolved.service as Service)
const heroImage = computed(() => service.value.seo?.og_image || service.value.featured_image || '')
const contentHtml = useContentHtml(service.value.path)

const crumbs = computed(() => [
  { label: 'Accueil', path: '/' },
  { label: service.value.title, path: service.value.path },
])

const stats = [
  { value: '+50', label: "années d'expérience depuis 1972" },
  { value: '2', label: 'agences : Mayenne et La Baule' },
  { value: `${settings.rating.value}/5`, label: `note Google (${settings.rating.count} avis)` },
]

useHead(() => {
  const s = service.value
  return {
    ...baseHead({
      title: s.seo?.seo_title || s.title,
      description: s.seo?.seo_description || s.intro,
      canonical: s.seo?.canonical || s.path,
      image: heroImage.value,
    }),
    script: [breadcrumbLd(crumbs.value)],
  }
})
</script>

<template>
  <div class="qsn">
    <section class="qsn-hero">
      <div class="container qsn-hero__in">
        <div>
          <span class="eyebrow">Depuis {{ settings.since }}</span>
          <h1>{{ service.h1 || service.title }}</h1>
          <p v-if="service.hero_subtitle" class="qsn-hero__sub">{{ service.hero_subtitle }}</p>
          <router-link :to="PATHS.contact" class="btn btn--primary">Nous contacter</router-link>
        </div>
        <img
          v-if="heroImage"
          :src="heroImage"
          :alt="service.h1 || service.title"
          class="qsn-hero__img"
          width="560"
          height="420"
        />
      </div>
    </section>

    <BreadcrumbBar :crumbs="crumbs" />

    <section class="section">
      <div class="container">
        <div class="qsn-stats">
          <div v-for="s in stats" :key="s.label" class="qsn-stat">
            <strong>{{ s.value }}</strong>
            <span>{{ s.label }}</span>
          </div>
        </div>
        <div class="prose prose--wide qsn-content" v-html="contentHtml"></div>
      </div>
    </section>

    <MatterportEmbed title="Visitez notre showroom en 3D" />

    <LabelsBand />

    <CtaProjet />
  </div>
</template>

<style scoped>
.qsn-hero {
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
  padding: 48px 0;
}
.qsn-hero__in {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 40px;
  align-items: center;
}
.qsn-hero__sub {
  font-size: 1.08rem;
  color: var(--ink);
  margin: 12px 0 22px;
}
.qsn-hero__img {
  width: 100%;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  object-fit: cover;
}
.qsn-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}
.qsn-stat {
  text-align: center;
  background: var(--green-soft);
  border-radius: var(--radius);
  padding: 24px 16px;
}
.qsn-stat strong {
  display: block;
  font-family: var(--font-title);
  font-size: 2rem;
  color: var(--green-dark);
}
.qsn-stat span {
  color: var(--ink);
  font-size: 0.9rem;
}
.qsn-content {
  margin: 0 auto;
}
@media (max-width: 860px) {
  .qsn-hero__in {
    grid-template-columns: 1fr;
  }
  .qsn-hero__img {
    order: -1;
  }
  .qsn-stats {
    grid-template-columns: 1fr;
  }
}
</style>
